import {
  formatOdds,
  resolveApiBase,
  SportsApiClient,
  type OddsFormat,
  type WidgetProps,
} from '@sports/core'
import { useState } from 'preact/hooks'
import { useFeed } from '../../use-feed'

export const TOPSCORER_ATTRIBUTES = [
  'market',
  'lang',
  'context',
  'heading',
  'limit',
  'refresh',
  'api-base',
  'theme',
] as const

interface TopscorerProps extends WidgetProps {
  market?: string
  lang?: string
  context?: string
  heading?: string
  limit?: string
  refresh?: string
  apiBase?: string
}

export const topscorerStyles = `
  .row {
    display: grid;
    grid-template-columns: 1.5rem 1fr auto;
    gap: 0.6rem;
    align-items: center;
    padding: 0.45rem 1rem;
    border-top: 1px solid var(--sports-border);
    font-size: 0.9rem;
  }
  .rank { color: var(--sports-muted); font-variant-numeric: tabular-nums; text-align: right; }
  .name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .prices { display: flex; gap: 0.4rem; }
  .pill {
    min-width: 3.2rem; text-align: center; padding: 0.2rem 0.45rem; border-radius: 6px;
    border: 1px solid var(--sports-border); font-variant-numeric: tabular-nums;
    text-decoration: none; color: var(--sports-fg); background: var(--sports-bg);
  }
  .pill.best { background: #fde68a; color: #111318; border-color: #f5cf5b; }
  .pill.empty { color: var(--sports-muted); }
  .cols { display: flex; justify-content: flex-end; gap: 0.4rem; padding: 0.35rem 1rem;
    font-size: 0.7rem; color: var(--sports-muted); border-top: 1px solid var(--sports-border); }
  .col { min-width: 3.2rem; text-align: center; }
  .brand { display: flex; align-items: center; gap: 0.4rem; }
  .brand img { height: 16px; }
  .see-more {
    display: block; width: 100%; padding: 0.6rem 1rem; text-align: center;
    background: var(--sports-card); color: var(--sports-muted);
    border: none; border-top: 1px solid var(--sports-border);
    font: inherit; font-size: 0.82rem; cursor: pointer;
  }
  .see-more:hover { color: var(--sports-fg); }
`

interface OutrightResponse {
  label: string
  bookmakers: string[]
  multi: boolean
  rows: (Record<string, number | string> & { name: string })[]
}
interface BookmakerResponse {
  oddsFormat: OddsFormat
  brand: { name?: string; logo?: string | null; link?: string | null } | null
}

function number(value: string | undefined, fallback: number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

/**
 * Top-scorer table — a ranked outright market (player ⟶ odds). Shares the
 * outright endpoint and the per-locale brand/format resolution; renders a rank
 * column and the same single- or multi-bookmaker price layout, best highlighted.
 * (Player photos + goals slot in later from a stats/image source.)
 */
export function Topscorer({
  market = '',
  lang = 'es',
  context = 'outright',
  heading,
  limit,
  refresh,
  apiBase,
}: TopscorerProps) {
  const base = resolveApiBase(apiBase)
  const max = number(limit, 20)
  const refreshSeconds = number(refresh, 300)
  const client = new SportsApiClient(base)
  const [expanded, setExpanded] = useState(false)

  const { data, error, loading } = useFeed(
    (signal) => client.outright<OutrightResponse>(market, lang, signal),
    [base, market, lang],
    refreshSeconds,
  )
  const { data: display } = useFeed(
    (signal) => client.bookmaker<BookmakerResponse>(lang, context, signal),
    [base, lang, context],
    0,
  )

  const format: OddsFormat = display?.oddsFormat ?? 'decimal'
  const brand = display?.brand ?? null
  const link = brand?.link ?? undefined
  const bookmakers = data?.bookmakers ?? []
  const rows = (data?.rows ?? []).slice(0, max)
  // Show the first 10 rows by default; the "Ver más" footer reveals the rest.
  const visible = expanded ? rows : rows.slice(0, 10)

  const priceOf = (row: Record<string, number | string>, book: string): number => {
    const value = row[book]
    return typeof value === 'number' ? value : Number(value)
  }
  const bestFor = (row: Record<string, number | string>): number =>
    Math.min(...bookmakers.map((b) => priceOf(row, b)).filter((p) => Number.isFinite(p) && p > 1))

  const pill = (value: number, best: boolean) => {
    const text = formatOdds(value, format)
    const cls = `pill${best ? ' best' : ''}${text ? '' : ' empty'}`
    return link && text ? (
      <a class={cls} href={link} target="_blank" rel="sponsored noopener">
        {text}
      </a>
    ) : (
      <span class={cls}>{text || '—'}</span>
    )
  }

  return (
    <div class="sports-card">
      <div class="sports-head">
        <span>{heading ?? data?.label ?? market}</span>
        {brand?.logo ? (
          <span class="brand">
            <img src={brand.logo} alt={brand.name ?? ''} />
          </span>
        ) : (
          brand?.name && <small>{brand.name}</small>
        )}
      </div>

      {loading && <div class="sports-note">Loading…</div>}
      {!loading && error && (
        <div class="sports-note error">Odds are unavailable right now.</div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div class="sports-note">No odds available.</div>
      )}

      {data?.multi && rows.length > 0 && (
        <div class="cols">
          <span style="flex:1"></span>
          {bookmakers.map((book) => (
            <span class="col" key={book}>
              {book}
            </span>
          ))}
        </div>
      )}

      {visible.map((row, index) => {
        const best = data?.multi ? bestFor(row) : NaN
        return (
          <div class="row" key={row.name}>
            <span class="rank">{index + 1}</span>
            <span class="name">{row.name}</span>
            <span class="prices">
              {data?.multi
                ? bookmakers.map((book) => {
                    const price = priceOf(row, book)
                    return <span key={book}>{pill(price, price === best)}</span>
                  })
                : pill(priceOf(row, bookmakers[0]), false)}
            </span>
          </div>
        )
      })}

      {rows.length > 10 && (
        <button class="see-more" type="button" onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Ver menos' : `Ver más (${rows.length})`}
        </button>
      )}
    </div>
  )
}
